// GERADO de backend-senne/src/contracts — NÃO editar; rode scripts/sync-contracts.sh

import { z } from 'zod'

/**
 * The patient's invoices (spec 20261105-090000, DEV-349): uploaded by hand in the ERP, seen by their holder in the exam
 * portal's Financeiro area. No schema here carries a CPF in a response: the tomador's CPF only finds the person (AC-3),
 * and a person found is shown by name, birth date and a masked document.
 */

/** AC-2: one PDF, required, and its XML, optional, each checked by content on the server. */
export const INVOICE_LIMITS = {
    pdfBytes: 10 * 1024 * 1024,
    xmlBytes: 1024 * 1024,
    /** The ERP's latest uploads, newest first (AC-7). */
    recentCount: 50,
    /** The holder search, as the scheduling patient search (spec 20261016-092045). */
    holderSearchCount: 10,
} as const

export const INVOICE_FILE_KINDS = ['pdf', 'xml'] as const
export const invoiceFileKindSchema = z.enum(INVOICE_FILE_KINDS)
export type InvoiceFileKind = z.infer<typeof invoiceFileKindSchema>

/** The codes of the spec's Contract section, sent in the error envelope's `error`. */
export const INVOICE_ERROR_CODES = {
    fileInvalid: 'INVOICE_FILE_INVALID',
    fileTooLarge: 'INVOICE_FILE_TOO_LARGE',
    holderNotFound: 'INVOICE_HOLDER_NOT_FOUND',
    attendanceNotFound: 'INVOICE_ATTENDANCE_NOT_FOUND',
    guardianNotConfirmed: 'INVOICE_GUARDIAN_NOT_CONFIRMED',
    duplicate: 'INVOICE_DUPLICATE',
    notFound: 'INVOICE_NOT_FOUND',
} as const
export type InvoiceErrorCode =
    (typeof INVOICE_ERROR_CODES)[keyof typeof INVOICE_ERROR_CODES]

const DAY = /^\d{4}-\d{2}-\d{2}$/

/** A civil day, `YYYY-MM-DD`, that exists in the calendar. */
export const invoiceDaySchema = z
    .string()
    .regex(DAY, 'Use a data no formato AAAA-MM-DD.')
    .refine((value) => {
        // Slices, not a destructured split: the exam portal compiles this file with `noUncheckedIndexedAccess`.
        const y = Number(value.slice(0, 4))
        const m = Number(value.slice(5, 7))
        const d = Number(value.slice(8, 10))
        const date = new Date(Date.UTC(y, m - 1, d))
        return (
            date.getUTCFullYear() === y &&
            date.getUTCMonth() === m - 1 &&
            date.getUTCDate() === d
        )
    }, 'Data inválida.')

/** The invoice number as the city prints it: digits, letters and the separators some cities use. */
export const invoiceNumberSchema = z
    .string()
    .trim()
    .min(1, 'Informe o número da nota.')
    .max(20, 'O número da nota pode ter no máximo 20 caracteres.')
    .regex(
        /^[0-9A-Za-z./-]+$/,
        'O número da nota aceita só letras, números, ponto, barra e hífen.'
    )

/** A person who can hold an invoice, as the ERP shows them before the uploader confirms (AC-3, AC-4). */
export const invoiceHolderSchema = z
    .object({
        person_id: z.number().int().positive(),
        name: z.string(),
        birth_date: z.string().regex(DAY).nullable(),
        /** The CPF with only digits 4 to 6 showing (`***.456.***-**`), or the passport's last 3; null with neither. */
        document: z.string().nullable(),
        /** The person can log in to the exam portal as a patient, so they will see the invoice (plan, Risks). */
        has_patient_account: z.boolean(),
    })
    .strict()
export type InvoiceHolder = z.infer<typeof invoiceHolderSchema>

/** `POST /api/erp/invoices/preview` answers this, inside the `data` envelope (AC-3). */
export const invoicePreviewSchema = z
    .object({
        invoice_number: z.string().nullable(),
        issued_on: z.string().regex(DAY).nullable(),
        amount_cents: z.number().int().nonnegative().nullable(),
        /** The XML named a tomador by CPF. False when it named none, or a CNPJ. */
        tomador_cpf_found: z.boolean(),
        /** The core person with the tomador's CPF; null when the XML named none or no person has it (AC-4). */
        holder: invoiceHolderSchema.nullable(),
    })
    .strict()
export type InvoicePreview = z.infer<typeof invoicePreviewSchema>

/** `GET /api/erp/invoices/holders`: exactly one of a CPF or at least 3 letters of a name. Messages never echo input. */
export const invoiceHolderSearchQuerySchema = z
    .object({
        cpf: z
            .string()
            .trim()
            .transform((value) => value.replace(/[.-]/g, ''))
            .refine(
                (value) => /^\d{11}$/.test(value),
                'Informe o CPF com 11 números.'
            )
            .optional(),
        name: z
            .string()
            .trim()
            .min(3, 'Digite ao menos 3 caracteres do nome.')
            .max(100, 'O nome pode ter no máximo 100 caracteres.')
            .optional(),
    })
    .strict()
    .refine(
        (query) => (query.cpf === undefined) !== (query.name === undefined),
        'Informe o CPF ou o nome.'
    )
export type InvoiceHolderSearchQuery = z.infer<
    typeof invoiceHolderSearchQuerySchema
>

/** `GET /api/erp/invoices/holders` answers this, inside the `data` envelope. */
export const invoiceHolderListSchema = z
    .object({ items: z.array(invoiceHolderSchema) })
    .strict()

/** `GET /api/erp/invoices/attendances/:number?holder=` answers this (AC-5). */
export const invoiceAttendanceSchema = z
    .object({
        attendance_number: z.number().int().positive(),
        entered_on: z.string().regex(DAY),
        hospital_name: z.string(),
        /** The attendance's patient, so the uploader can tell a newborn's attendance from the holder's own. */
        patient_name: z.string(),
        /** False is the newborn case: the upload then needs `guardian_confirmed` (AC-5). */
        patient_is_holder: z.boolean(),
    })
    .strict()
export type InvoiceAttendance = z.infer<typeof invoiceAttendanceSchema>

export const invoiceAttendanceQuerySchema = z
    .object({
        holder: z.coerce
            .number({ invalid_type_error: 'Titular inválido.' })
            .int('Titular inválido.')
            .positive('Titular inválido.'),
    })
    .strict()

/**
 * `POST /api/erp/invoices`: the multipart part `fields`, a JSON text. JSON and not one part per field, because a
 * multipart value is always text and `z.coerce.boolean()` reads `'false'` as true.
 */
export const invoiceUploadFieldsSchema = z
    .object({
        holder_person_id: z.number().int().positive('Titular inválido.'),
        attendance_number: z
            .number()
            .int('Número de atendimento inválido.')
            .positive('Número de atendimento inválido.'),
        invoice_number: invoiceNumberSchema,
        issued_on: invoiceDaySchema,
        amount_cents: z
            .number()
            .int('Valor inválido.')
            .positive('Informe o valor da nota.')
            .max(10_000_000_000, 'Valor acima de R$ 100.000.000,00.'),
        /** AC-5: required to be true when the attendance's patient is not the holder. */
        guardian_confirmed: z.boolean().default(false),
    })
    .strict()
export type InvoiceUploadFields = z.infer<typeof invoiceUploadFieldsSchema>

/** One upload as the ERP lists it (AC-7). `POST /api/erp/invoices` answers one, 201. */
export const uploadedInvoiceSchema = z
    .object({
        invoice_uuid: z.string().uuid(),
        invoice_number: z.string(),
        issued_on: z.string().regex(DAY),
        amount_cents: z.number().int().positive(),
        attendance_number: z.number().int().positive(),
        holder_person_id: z.number().int().positive(),
        holder_name: z.string(),
        has_xml: z.boolean(),
        uploaded_at: z.string(),
        uploaded_by_name: z.string(),
    })
    .strict()
export type UploadedInvoice = z.infer<typeof uploadedInvoiceSchema>

export const uploadedInvoiceListSchema = z
    .object({ items: z.array(uploadedInvoiceSchema) })
    .strict()

/** `GET /api/me/invoices`: every filter optional, each narrowing (AC-9). */
export const myInvoicesQuerySchema = z
    .object({
        from: invoiceDaySchema.optional(),
        to: invoiceDaySchema.optional(),
        attendance: z.coerce
            .number({ invalid_type_error: 'Número de atendimento inválido.' })
            .int('Número de atendimento inválido.')
            .positive('Número de atendimento inválido.')
            .optional(),
    })
    .strict()
    .refine(
        (query) => !query.from || !query.to || query.from <= query.to,
        'A data inicial deve ser anterior à final.'
    )
export type MyInvoicesQuery = z.infer<typeof myInvoicesQuerySchema>

/** One invoice as its holder sees it (AC-9): no name, no document, no uploader. */
export const myInvoiceSchema = z
    .object({
        invoice_uuid: z.string().uuid(),
        invoice_number: z.string(),
        issued_on: z.string().regex(DAY),
        amount_cents: z.number().int().positive(),
        attendance_number: z.number().int().positive(),
        has_xml: z.boolean(),
    })
    .strict()
export type MyInvoice = z.infer<typeof myInvoiceSchema>

/** `GET /api/me/invoices` answers this, inside the `data` envelope, the newest issue date first. */
export const myInvoiceListSchema = z
    .object({ items: z.array(myInvoiceSchema) })
    .strict()
export type MyInvoiceList = z.infer<typeof myInvoiceListSchema>
